import i18n from "@/modules/i18n"
import enabledPaymentMethods from "@/utils/payment-methods"

function productName(state, { version = 1 } = {}) {
  if (state.productType === "subscriptions") {
    const key = `checkout.subscriptions.v${version}`
    const nameKey = `${state.plan.tier}.name`

    return i18n.t(`${key}.product`, {
      period: i18n.t(`${key}.period.${state.plan.type}`),
      name: i18n.t(`${key}.${nameKey}`)
    })
  }
  if (state.productType === "teams") {
    return i18n.t("checkout.teams.product", {
      teamSize: state.plan.teamSize
    })
  }
  if (state.plan.name) {
    return state.plan.name
  }
  return state.productType
}

function formatPrice(number, currency, locale) {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency
  }).format(number)
}

export default {
  isTeamPurchase: (state) => {
    return state.productType === "teams"
  },
  productName: (state) => {
    return productName(state, { version: 1 })
  },
  productNameV2: (state) => {
    return productName(state, { version: 2 })
  },
  productMessage: (state) => {
    const key = "checkout.subscriptions.v2"
    if (state.productType === "subscriptions") {
      return i18n.t(`${key}.messageBox.${state.plan.type}`)
    }
    // giftcards or teams (i.e. no trial)
    return i18n.t(`${key}.messageBox.monthly`)
  },
  spelledCurrency: (state) => {
    return i18n.t(`checkout.currencies.${state.plan.currency.toLowerCase()}`)
  },
  disclaimer: (_, getters) => {
    return i18n.t("checkout.disclaimer.label", {
      currency: getters.spelledCurrency
    })
  },
  paymentMethods: (state) => {
    return (
      state.plan.currency &&
      enabledPaymentMethods({
        currency: state.plan.currency,
        location: state.countryCode
      })
    )
  },
  sortedPaymentMethods: (state, getters) => {
    return Object.entries(getters.paymentMethods).sort((key) => {
      if (key === "paypal") {
        return -1
      }

      return 0
    })
  },
  showBilledAfterTrial: () => {
    return false
  },
  hasTrial: (state) => {
    return !!state.plan.trialDays
  },
  couponAvailable: (state) => {
    return state.productType === "subscriptions" || state.productType === "teams"
  },
  hasCoupon: (state) => {
    return !!state.coupon.type
  },
  couponCode: (state) => {
    return state.coupon.code
  },
  couponDiscount: (state) => {
    return state.coupon.type === "percent" && state.coupon.value
  },
  hasTax: (state) => {
    return !!state.tax.amount
  },
  taxRegion: (state) => {
    return state.tax.region
  },
  taxAmount: (state) => {
    return state.tax.amount
  },
  couponTrialDays: (state) => {
    return state.coupon.type === "free_trial" && state.coupon.value
  },
  twoForOne: (state) => {
    return state.coupon.type === "two_for_one"
  },
  trialDays: (state, getters) => {
    if (!getters.hasTrial) {
      return
    }
    return state.plan.trialDays
  },
  teamOwnerPrice: (state, getters) => {
    if (state.productType !== "teams") {
      return undefined
    }

    return getters["freeMarkupV2"]
  },
  aboDuration: (state, getters) => {
    if (!getters.hasTrial) {
      return
    }
    return i18n.t(`checkout.subscriptions.aboDuration.${state.plan.duration}`)
  },
  duration: (state) => {
    return state.plan.duration
  },
  summaryDescriptionI18nKey: (state, getters) => {
    if (getters.hasTrial) {
      return "trial"
    }

    return "default"
  },
  submitI18nKey: (state) => {
    if (state.sending) {
      return "sending"
    }
    if (state.plan.trialDays) {
      return "trial"
    }
    if (["subscriptions", "teams"].includes(state.productType)) {
      return "subscribe"
    }
    return "default"
  },
  disclaimerI18nKey: (state) => {
    if (state.plan.price.discount && state.plan.trialDays) {
      return state.coupon.isSingleUse ? "trialDiscount" : "trialDiscountForever"
    }

    if (state.plan.price.discount) {
      return "discount"
    }

    if (state.plan.trialDays) {
      return "trial"
    }
    if (state.productType === "subscriptions") {
      return "subscribe"
    }
    if (state.productType === "teams") {
      return "teams"
    }
    return "default"
  },
  trialEndDate: (state) => {
    const date = new Date()
    date.setDate(date.getDate() + state.plan.trialDays)
    return i18n.d(date, "default")
  },
  trialLastDay: (state) => {
    const date = new Date()
    date.setDate(date.getDate() + state.plan.trialDays - 1)
    return i18n.d(date, "default")
  },
  originalPrice: (state, getters) => {
    if (state.plan.price.original && state.plan.price.original.markup) {
      return state.plan.price.original.markup
    }
    return getters["finalPrice"]
  },
  originalPriceV2: (state, getters) => {
    if (state.plan.price.original && state.plan.price.original.value) {
      return formatPrice(state.plan.price.original.value, state.plan.currency, i18n.locale)
    }
    return getters["finalPriceV2"]
  },
  finalPrice: (state) => {
    if (state.plan.price.final && state.plan.price.final.markup) {
      return state.plan.price.final.markup
    }
    return "-"
  },
  finalPriceV2: (state) => {
    if (state.plan.price.final && state.plan.price.final.value) {
      return formatPrice(state.plan.price.final.value, state.plan.currency, i18n.locale)
    }
    return "-"
  },
  freeMarkup: (state) => {
    if (state.plan.freeMarkup) {
      return state.plan.freeMarkup
    }
    return "-"
  },
  freeMarkupV2: (state) => {
    if (!state.plan.currency) {
      return "-"
    }

    return formatPrice(0.0, state.plan.currency, i18n.locale)
  },
  discountAmount: (state) => {
    if (state.plan.price.discount && state.plan.price.discount.markup) {
      return state.plan.price.discount.markup
    }
    return "-"
  },
  discountAmountV2: (state) => {
    if (state.plan.price.discount && state.plan.price.discount.value) {
      const negativeDiscountValue = state.plan.price.discount.value * -1
      return formatPrice(negativeDiscountValue, state.plan.currency, i18n.locale)
    }
    return "-"
  },
  formattedPrice: (state) => {
    return state.plan && state.plan.price.final.markup
  },
  formattedSubsequentPrice: (state) => {
    if (!state.coupon || !state.plan) {
      return ""
    }

    const foreverDiscount = state.plan.discount && !state.coupon.isSingleUse
    return foreverDiscount ? state.plan.price.final.markup : state.plan.price.original.markup
  },
  isCountryUS: (state) => {
    return state.countryCode === "us"
  },
  isRegionRequired: (_, getters) => {
    return getters.countryRegions.length > 0
  },
  countryCode: (state) => {
    return state.countryCode
  },
  countryRegions: (state) => {
    if (!state.regionsByCountry) {
      return []
    }

    const countryInfo = state.regionsByCountry.find((info) => info.country === state.countryCode)

    return countryInfo ? countryInfo.regions : []
  },
  b2bTrailEnds: (state) => {
    return state.b2bTrailEnds
  }
}
